import { useState } from "react";

import { Colors, Divider } from "@blueprintjs/core";

import design from "@/../../hypetrain/static/img/program/design.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Box, Flex } from "@/components/layout/flexbox";
import { useAuth } from "@/components/lib/auth";
import { AddProgramComponentDialog } from "@/components/pages_logged_in/program/components/AddProgramComponentDialog";
import {
  getActiveComponentTypes,
  ProgramComponentCreationType,
} from "@/components/pages_logged_in/program/components/set-components/ChooseType";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { CARD_CLASSES, MIDTEXT_COLOR, SUBTEXT_COLOR } from "@/css/constants";
import { ComponentTypeEnum } from "@/graphql";
import { MainProgram } from "@/types/programs";

interface ProgramComponentGridProps {
  programComponentTypes: Array<ProgramComponentCreationType>;
  onSelectComponentType: (programComponentType: ComponentTypeEnum) => void;
}

function ProgramComponentGrid({
  programComponentTypes,
  onSelectComponentType,
}: ProgramComponentGridProps) {
  return (
    <Flex flexWrap="wrap" mx={-12}>
      {programComponentTypes.map((programComponentType, index) => (
        <Clickable
          key={`component-type-${index}`}
          onClick={() => onSelectComponentType(programComponentType?.value)}
        >
          <Flex
            px={16}
            py={24}
            m={12}
            className={CARD_CLASSES}
            css={{
              cursor: "pointer",
              ":hover": { background: Colors.LIGHT_GRAY5 },
            }}
            bg={Colors.WHITE}
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            width={[1, "calc(50% - 24px)", "calc(33.33% - 24px)"]}
          >
            <LazyImg
              src={programComponentType.icon}
              alt={programComponentType.name}
              height={50}
              width={50}
            />
            <Box
              mt={12}
              fontSize={16}
              fontWeight={600}
              color={MIDTEXT_COLOR}
              textAlign="center"
            >
              {programComponentType.name}
            </Box>
            <Box fontSize={14} color={SUBTEXT_COLOR} textAlign="center">
              {programComponentType.description}
            </Box>
          </Flex>
        </Clickable>
      ))}
    </Flex>
  );
}

interface DesignInitProgramPanelProps {
  program: MainProgram;
  programComponentTypes: ProgramComponentCreationType[];
  onSelectComponentType: (programComponentType: ComponentTypeEnum) => void;
}

function DesignInitProgramPanel({
  program,
  programComponentTypes,
  onSelectComponentType,
}: DesignInitProgramPanelProps) {
  const hasComponents = program?.hasComponents;

  if (hasComponents || !program) {
    return null;
  }

  return (
    <Box>
      <Box
        flex={1}
        px={[16, 24]}
        pt={[16, 30]}
        pb={[0, 14]}
        my={36}
        className={CARD_CLASSES}
        css={{ background: Colors.WHITE }}
      >
        <Flex pb={24} className="bbs" alignItems="center">
          <Flex alignItems={"center"} width={"100%"}>
            <EmbedSvg src={design} height={50} width={50} />
            <Box mx={12} flex={1}>
              <Box fontSize={20} fontWeight={600}>
                Design Your Program
              </Box>
              <Box fontSize={16} color={MIDTEXT_COLOR}>
                Build your program by adding components
              </Box>
            </Box>
          </Flex>
        </Flex>
        <ProgramComponentGrid
          programComponentTypes={programComponentTypes}
          onSelectComponentType={onSelectComponentType}
        />
      </Box>
      <Divider />
    </Box>
  );
}

interface ProgramDashboardInitComponentsProps {
  program: MainProgram;
}

export function ProgramDashboardInitComponents({
  program,
}: ProgramDashboardInitComponentsProps) {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<ComponentTypeEnum>();
  const { currentWorkspace } = useAuth();
  const activeComponentTypes = getActiveComponentTypes(
    currentWorkspace.urlSlug,
  );

  const closeDialog = () => {
    setOpen(false);
    setSelectedType(undefined);
  };

  return (
    <Box>
      <DesignInitProgramPanel
        program={program}
        programComponentTypes={activeComponentTypes}
        onSelectComponentType={(programComponentType) => {
          setSelectedType(programComponentType);
          setOpen(true);
        }}
      />
      <AddProgramComponentDialog
        programId={program.id}
        open={open}
        onClose={closeDialog}
        componentType={selectedType}
      />
    </Box>
  );
}
